import { findCurriculumRegistryEntry, getCurriculumRegistry } from "@/config/curricula.registry";
import type { Goal } from "@/models/Goal.model";

export interface CurriculumGoalGroup {
	/** Registry key, or the lower-cased raw source for curricula the registry does not know. */
	key: string;
	curriculum: Curriculum;
	goals: Goal[];
}

/** School's curriculum first, registry order next, unknown sources last. */
function groupRank(group: CurriculumGoalGroup, schoolKey: string | null): number {
	if (schoolKey && group.key === schoolKey) {
		return -1;
	}

	const registry = getCurriculumRegistry();
	const index = registry.findIndex((entry) => entry.key === group.key);
	return index === -1 ? registry.length : index;
}

/**
 * What a goal-count badge shows: parent goals only. Subgoals always travel with their
 * parent (muted or not), so counting them would inflate every number the moment a
 * GO! Navigator goal with a dozen MIA elements is picked.
 */
export function countParentGoals(goals: Goal[]): number {
	return goals.filter((goal) => !goal.isSubgoal()).length;
}

export class Curriculum
{
	public readonly key: string | null = null;
	public readonly organisation: string | null = null;
	public readonly country: string | null = null;
	public readonly countryFlag: string | null = null;
	public readonly locales: string[] = [];
	public readonly externalPicker: string | null = null;
	public readonly inlineViewer: boolean = false;

	private readonly registryName: string | null = null;

	/**
	 * @param id the organisation's raw curriculum id (e.g. "OpStap", "GO_2023").
	 * Unknown ids degrade to a fallback instance: id as name, no picker, no viewer —
	 * still listed and handled by the generic goal selector drawer.
	 */
	constructor(
		public id: string
	) {
		const entry = findCurriculumRegistryEntry(id);
		if (entry) {
			this.key = entry.key;
			this.registryName = entry.name;
			this.organisation = entry.organisation;
			this.country = entry.country;
			this.countryFlag = entry.countryFlag;
			this.locales = entry.locales;
			this.externalPicker = entry.externalPicker;
			this.inlineViewer = entry.inlineViewer;
		}
	}

	get name(): string {
		return this.registryName ?? this.id;
	}

	/** Display label for source menus: "Op.stap (KOV)". */
	get menuLabel(): string {
		return this.organisation ? `${this.name} (${this.organisation})` : this.name;
	}

	hasExternalGoalPicker(): boolean {
		return this.externalPicker !== null;
	}

	hasInlineGoalViewer(): boolean {
		return this.inlineViewer;
	}

	/**
	 * Whether a goal belongs to this curriculum: its `source` exactly equals this curriculum's
	 * id. Versions are distinct curricula (ZiLL_2023 is not ZiLL) — this is the same rule
	 * `Goals.store.curriculumItems` applies.
	 */
	containsGoal(goal: Goal): boolean {
		return !!goal.source && goal.source === this.id;
	}

	/**
	 * Keep only the goals a school may work with: those of its own curriculum.
	 *
	 * Lesson plans can carry goals from other curricula (external plans from publishers, or
	 * plans kept after a school switched curriculum), but a teacher must never see or evaluate
	 * those. With no curriculum configured nothing may be evaluated, so the result is empty.
	 */
	static filterGoals(goals: Goal[], schoolCurriculum: Curriculum | null): Goal[] {
		if (!schoolCurriculum) {
			return [];
		}
		return goals.filter((goal) => schoolCurriculum.containsGoal(goal));
	}

	/**
	 * Group goals by the curriculum they come from, ordered for display.
	 *
	 * Shared by the editable field (GoalsField) and the read-only display (GoalsDisplay) so a
	 * lesson plan's goals are sectioned and ordered identically whether you are editing or
	 * viewing it. Goals keep their input order within a group — callers sort if they need to.
	 *
	 * @param goals the goals to group
	 * @param schoolCurriculum the current school's curriculum, which only affects ordering
	 */
	static groupGoals(
		goals: Goal[],
		schoolCurriculum: Curriculum | null = null,
	): CurriculumGoalGroup[] {

		const groups = new Map<string, CurriculumGoalGroup>();

		for (const goal of goals) {
			const source = goal.source ?? '';
			const curriculum = new Curriculum(source);
			const key = curriculum.key ?? source.toLowerCase();

			if (!groups.has(key)) {
				groups.set(key, { key, curriculum, goals: [] });
			}
			groups.get(key)!.goals.push(goal);
		}

		const schoolKey = schoolCurriculum
			? (schoolCurriculum.key ?? schoolCurriculum.id.toLowerCase())
			: null;

		return [...groups.values()].sort(
			(a, b) => groupRank(a, schoolKey) - groupRank(b, schoolKey)
				|| a.curriculum.id.localeCompare(b.curriculum.id)
		);
	}
}
